import { DynamoDbPolicy, DynamoDbPolicyDetails } from '@ourbranch/schema-types/dynamodb';
import cloneDeep from 'lodash.clonedeep';
import dayjs from 'dayjs';

type makePolicyDetailsFromSegmentsOptions = {
  activeSegmentsOnly?: boolean;
  activeOnDate?: string;
  ignoreRenewalSegments?: boolean;
  upToSegmentIndex?: number;
  upToSegmentId?: string;
  keepPremium?: boolean;
};

const normalizedProviderDataArrayKeys = [
  'branch',
  'cape',
  'cherre',
  'coreLogic',
  'input',
  'smartyStreets',
  'verisk',
  'veriskRoofYear'
];

/**
 * Reconstructs full policy details from farm policy segments using delta-based approach
 * For farm policies:
 * - Segment 0: Contains full policy details (base)
 * - Segments 1+: Contains only delta changes (except renewal segments which are ignored)
 * @param policy The farm policy
 * @param segments Array of policy segments
 * @param upToSegmentIndex Optional index to stop at (for partial reconstruction)
 * @returns Complete policy details with all segments applied
 */
export function makePolicyDetailsFromSegments(
  policy: DynamoDbPolicy,
  segments: DynamoDbPolicyDetails[],
  options?: makePolicyDetailsFromSegmentsOptions
): DynamoDbPolicyDetails {
  const {
    activeSegmentsOnly = false,
    activeOnDate = '',
    ignoreRenewalSegments = true,
    upToSegmentIndex = undefined,
    upToSegmentId = undefined,
    keepPremium = false
  } = options || {};

  if (!segments || segments.length === 0) {
    throw new Error('Cannot make policy details from empty segments array');
  }

  // Resolve the stop index once, up front. When the caller targets the inception
  // segment (upToSegmentId matching segments[0]), stopIndex is 0 and no deltas
  // get applied — preserving the inception state.
  let stopIndex = segments.length - 1;
  if (upToSegmentId !== undefined) {
    const targetIndex = segments.findIndex((s) => s.segmentId === upToSegmentId);
    if (targetIndex >= 0) {
      stopIndex = targetIndex;
    }
  }
  if (upToSegmentIndex !== undefined) {
    stopIndex = Math.min(stopIndex, upToSegmentIndex);
  }

  // Start with the base segment (segment 0) - always contains full data
  const fullSegmentDetails: DynamoDbPolicyDetails = cloneDeep(segments[0]);

  // Apply delta segments in sequence (segments 1+ up to and including stopIndex)
  for (let i = 1; i <= stopIndex; i += 1) {
    if (activeSegmentsOnly && dayjs(segments[i].startDate).isAfter(dayjs())) {
      // Ignore any segments that start in the future if `activeSegmentsOnly` is true
      continue;
    }

    if (activeOnDate && dayjs(segments[i].startDate).isAfter(dayjs(activeOnDate))) {
      // for claims only show policy segments that are active on date of loss
      continue;
    }

    if (ignoreRenewalSegments && dayjs(segments[i].startDate).isAfter(policy.endDate)) {
      // ignore renewal segments
      continue;
    }

    // Apply the delta segment to the full segment details
    applyFarmObjectDelta(fullSegmentDetails, segments[i]);
  }

  if (fullSegmentDetails.premium && !keepPremium) {
    // we don't want the segment details premium to override policy.premium
    // and we copy the elements here to the root of policy
    delete (fullSegmentDetails as any).premium;
  }

  return fullSegmentDetails;
}

/**
 * Generic function to apply delta changes to an array of objects
 * @param baseArray The base array to apply changes to
 * @param deltaArray The array containing delta changes
 * @param idField The field name used to identify objects (default: 'id')
 */
function applyArrayDelta(baseArray: any[], deltaArray: any[], idField = 'id'): void {
  for (const deltaItem of deltaArray) {
    const itemId = deltaItem[idField];
    const existingItemIndex = baseArray.findIndex((item: any) => item[idField] === itemId);

    if (existingItemIndex >= 0) {
      if (deltaItem.isDeleted) {
        // Remove deleted items
        baseArray.splice(existingItemIndex, 1);
      } else {
        // Update existing item with delta changes
        applyFarmObjectDelta(baseArray[existingItemIndex], deltaItem);
      }
    } else {
      // Add new item (skip if it's marked as deleted)
      const newItem = cloneDeep(deltaItem);
      baseArray.push(newItem);
    }
  }
}

/**
 * Generic function to apply delta changes to an object
 * @param baseObject The base object to apply changes to
 * @param deltaObject The object containing delta changes
 */
function applyFarmObjectDelta(baseObject: any, deltaObject: any): void {
  for (const key in deltaObject) {
    if (key === 'isDeleted') {
      // Skip the isDeleted flag itself
      continue;
    }

    const deltaValue = deltaObject[key];

    if (deltaValue === null || deltaValue === undefined) {
      // Skip null/undefined values
      continue;
    }

    if (Array.isArray(deltaValue)) {
      // Handle array fields recursively
      if (!baseObject[key]) {
        baseObject[key] = [];
      }

      if (normalizedProviderDataArrayKeys.includes(key)) {
        applyArrayDelta(baseObject[key], deltaValue, 'attribute');
      } else {
        applyArrayDelta(baseObject[key], deltaValue, 'id');
      }
    } else if (typeof deltaValue === 'object' && !(deltaValue instanceof Date)) {
      // Handle nested object fields recursively
      if (!baseObject[key]) {
        baseObject[key] = {};
      }
      applyFarmObjectDelta(baseObject[key], deltaValue);
    } else {
      // Simple field - just copy the value
      baseObject[key] = deltaValue;
    }
  }
}
